/*
 * qk-upsell.js — puzzle variations and cart drawer upsell (snippets/qk-*.liquid).
 *
 * <qk-variation-picker>: swaps the product page to a sibling product without reloading, like a variant change.
 * <qk-bundle-picker>:    1 / 2 / 3 puzzle tiers on the product page; 2+ opens a design picker that adds all puzzles.
 * <qk-cart-upsell>:      inline puzzle picker for the cart drawer offers, adds the chosen puzzles.
 * <qk-line-swap>:        swaps a cart line for another puzzle design, keeping the quantity.
 *
 * Cart changes are broadcast with the Impact "cart:change" event (cart + rendered sections), so the cart drawer,
 * cart count and every other theme listener update exactly as with a native add to cart.
 */
(() => {
  const routesRoot = () => window.Shopify?.routes?.root || '/';

  const cartSectionIds = () => {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
    return sections;
  };

  /* Send a cart request with the bundled sections and broadcast the new cart like the theme does.
     With openDrawer, "variant:add" is dispatched too so the cart drawer opens as after a regular add to cart. */
  const updateCart = async (endpoint, body, { openDrawer = false } = {}) => {
    const response = await fetch(`${routesRoot()}cart/${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ ...body, sections: cartSectionIds() })
    });
    const responseJson = await response.json();

    if (!response.ok) {
      throw new Error(responseJson.description || responseJson.message || 'Could not update the cart');
    }

    const cart = await (await fetch(`${routesRoot()}cart.js`)).json();
    cart.sections = responseJson.sections;

    if (openDrawer) {
      document.documentElement.dispatchEvent(new CustomEvent('variant:add', {
        bubbles: true,
        detail: { items: responseJson.items || [responseJson], cart }
      }));
    }

    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: { baseEvent: 'variant:add', cart }
    }));

    return cart;
  };

  const formatMoney = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'USD'
  }).format(cents / 100);

  const sumPrices = (inputs) => inputs.reduce((total, input) => total + parseInt(input.dataset.price || '0', 10), 0);

  /* Keep at most "need" picks selected: a new pick replaces the oldest one. Returns the picks in selection order. */
  const limitPicks = (order, input, need) => {
    const nextOrder = order.filter((item) => item !== input);

    if (input.checked) {
      nextOrder.push(input);

      if (nextOrder.length > need) {
        nextOrder.shift().checked = false;
      }
    }

    return nextOrder;
  };

  /* ------------------------------------------------------------------------------------------------------------------
   * Product page: sibling products as variations
   * ---------------------------------------------------------------------------------------------------------------- */

  const preloadedSections = new Map();

  /* sessionStorage key: Mix & Match selection kept while the shopper looks at the bundles page. */
  const RETURN_KEY = 'qk-mixmatch-return';

  class QkVariationPicker extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      const signal = this._abortController.signal;

      this.addEventListener('click', this._onClick.bind(this), { signal });
      this.addEventListener('pointerenter', this._onPreload.bind(this), { capture: true, signal });
      this.addEventListener('keydown', this._onKeydown.bind(this), { signal });

      this._moreButton = this.querySelector('[data-qk-more]');
      this._moreButton?.addEventListener('click', this._onMoreClick.bind(this), { signal });
      this._resizeObserver = new ResizeObserver(() => {
        if (this.clientWidth !== this._lastWidth) {
          this._lastWidth = this.clientWidth;
          this._clampRows();
        }
      });
      this._resizeObserver.observe(this);

      if (!history.state?.qkProduct) {
        history.replaceState({ ...history.state, qkProduct: true }, '', location.href);
      }
    }

    disconnectedCallback() {
      this._abortController.abort();
      this._resizeObserver?.disconnect();
    }

    _onMoreClick() {
      const expanded = this._moreButton.getAttribute('aria-expanded') !== 'true';
      this._moreButton.setAttribute('aria-expanded', String(expanded));
      this._clampRows();
    }

    /* Hide the options below the first "data-rows" rows (the number per row depends on the width) until expanded. */
    _clampRows() {
      const options = Array.from(this.querySelectorAll('.qk-variations__option'));
      const rows = parseInt(this.dataset.rows || '0', 10);
      const expanded = this._moreButton?.getAttribute('aria-expanded') === 'true';

      options.forEach((option) => option.hidden = false);

      /* data-visible: show the first N designs (plus the selected one) instead of a number of rows. */
      const visible = parseInt(this.dataset.visible || '0', 10);

      if (visible && this._moreButton) {
        if (options.length <= visible) {
          this._moreButton.hidden = true;
          return;
        }

        if (!expanded) {
          options.forEach((option, index) => option.hidden = index >= visible && option.getAttribute('aria-checked') !== 'true');
        }

        this._moreButton.hidden = false;
        this._moreButton.textContent = expanded ? 'Show fewer designs' : `View all ${options.length} designs`;
        return;
      }

      const rowTops = [...new Set(options.map((option) => option.offsetTop))];

      if (!rows || !this._moreButton || rowTops.length <= rows) {
        if (this._moreButton) {
          this._moreButton.hidden = true;
        }
        return;
      }

      if (!expanded) {
        options.forEach((option) => option.hidden = option.offsetTop >= rowTops[rows] && option.getAttribute('aria-checked') !== 'true');
      }

      this._moreButton.hidden = false;
      this._moreButton.textContent = expanded ? 'Show fewer designs' : `View all ${options.length} designs`;
    }

    get sectionIds() {
      return (this.dataset.sections || '').split(',').filter((id) => id && document.getElementById(`shopify-section-${id}`));
    }

    _sectionsUrl(option) {
      const url = new URL(option.dataset.url, location.origin);

      if (option.dataset.view) {
        url.searchParams.set('view', option.dataset.view);
      }

      url.searchParams.set('sections', this.sectionIds.join(','));
      return url.toString();
    }

    _load(option) {
      const url = this._sectionsUrl(option);

      if (!preloadedSections.has(url)) {
        preloadedSections.set(url, fetch(url).then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to load ${url}`);
          }
          return response.json();
        }).catch((error) => {
          preloadedSections.delete(url);
          throw error;
        }));
      }

      return preloadedSections.get(url);
    }

    _onPreload(event) {
      const option = event.target.closest?.('.qk-variations__option');

      if (option && option.getAttribute('aria-checked') !== 'true') {
        this._load(option).catch(() => {});
      }
    }

    _onKeydown(event) {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) {
        return;
      }

      const options = Array.from(this.querySelectorAll('.qk-variations__option'));
      const index = options.indexOf(document.activeElement);

      if (index === -1) {
        return;
      }

      event.preventDefault();
      const next = options[(index + (event.key === 'ArrowRight' ? 1 : -1) + options.length) % options.length];
      next.focus();
    }

    async _onClick(event) {
      const option = event.target.closest('.qk-variations__option');

      if (!option || option.getAttribute('aria-checked') === 'true' || this.hasAttribute('aria-busy')) {
        return;
      }

      this.setAttribute('aria-busy', 'true');
      this.querySelectorAll('.qk-variations__option').forEach((item) => item.setAttribute('aria-checked', String(item === option)));

      try {
        const sections = await this._load(option);
        const sectionIds = this.sectionIds;

        sectionIds.forEach((sectionId) => {
          const current = document.getElementById(`shopify-section-${sectionId}`);
          const html = sections[sectionId];

          if (!current || !html) {
            return;
          }

          const fresh = new DOMParser().parseFromString(html, 'text/html').getElementById(`shopify-section-${sectionId}`);

          if (fresh) {
            current.replaceChildren(...Array.from(fresh.childNodes).map((node) => document.importNode(node, true)));
          }
        });

        history.pushState({ qkProduct: true }, '', option.dataset.pageUrl);
        document.title = option.dataset.pageTitle;

        window.Shopify?.PaymentButton?.init?.();
        window.yotpoWidgetsContainer?.initWidgets?.();

        document.dispatchEvent(new CustomEvent('qk:product-change', { detail: { url: option.dataset.pageUrl } }));
      } catch (error) {
        window.location.href = option.dataset.pageUrl;
      } finally {
        this.removeAttribute('aria-busy');
      }
    }
  }

  /* Back/forward between swapped products: reload the page that matches the URL. */
  window.addEventListener('popstate', (event) => {
    if (event.state?.qkProduct) {
      window.location.reload();
    }
  });

  /* ------------------------------------------------------------------------------------------------------------------
   * Cart drawer: upsell picker
   * ---------------------------------------------------------------------------------------------------------------- */

  class QkCartUpsell extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      const signal = this._abortController.signal;

      this.addEventListener('click', this._onClick.bind(this), { signal });
      this.addEventListener('change', this._onPickChange.bind(this), { signal });
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    get picker() {
      return this.querySelector('[data-qk-picker]');
    }

    get picks() {
      return Array.from(this.querySelectorAll('[data-qk-pick]'));
    }

    _onClick(event) {
      const openButton = event.target.closest('[data-qk-open]');

      if (openButton) {
        this._open(parseInt(openButton.dataset.qkOpen, 10));
      } else if (event.target.closest('[data-qk-close]')) {
        this.picker.hidden = true;
      } else if (event.target.closest('[data-qk-add]')) {
        this._add();
      }
    }

    /* Open the picker for "need" puzzles, preselecting the suggestions shown in the offer. */
    _open(need) {
      if (!this.picker) {
        return;
      }

      this._need = need;
      this._order = [];

      this.picks.forEach((input, index) => {
        input.checked = index < need;
        if (input.checked) {
          this._order.push(input);
        }
      });

      this.picker.querySelector('[data-qk-need]').textContent = need;
      this.picker.querySelector('[data-qk-noun]').textContent = need === 1 ? 'puzzle' : 'puzzles';
      this.picker.hidden = false;
      this._refreshButton();
      this.picker.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    _onPickChange(event) {
      if (event.target.matches('[data-qk-pick]')) {
        this._order = limitPicks(this._order || [], event.target, this._need);
        this._refreshButton();
      }
    }

    /* The button shows what the chosen puzzles add to the current total: bundle discount and shipping included. */
    _refreshButton() {
      const selected = this._order.length;
      const button = this.picker.querySelector('[data-qk-add]');
      const label = this.picker.querySelector('[data-qk-add-label]');

      button.disabled = selected === 0;

      if (selected === 0) {
        label.textContent = 'Select your puzzles';
        return;
      }

      const data = this.dataset;
      const count = parseInt(data.count, 10);
      const bundleQty = parseInt(data.bundleQty, 10);
      const reachesBundle = count < bundleQty && count + selected >= bundleQty;
      const newCartTotal = parseInt(data.cartTotal, 10) + sumPrices(this._order) - (reachesBundle ? parseInt(data.bundleDiscount, 10) : 0);
      const newShipping = data.requiresShipping === 'true' && newCartTotal < parseInt(data.freeShippingThreshold, 10) ? parseInt(data.shippingRate, 10) : 0;
      const delta = newCartTotal + newShipping - parseInt(data.currentTotal, 10);

      label.textContent = `Add ${selected} ${selected === 1 ? 'puzzle' : 'puzzles'} · +${formatMoney(delta)}`;
    }

    async _add() {
      const button = this.picker.querySelector('[data-qk-add]');
      const error = this.picker.querySelector('.qk-upsell__error');
      const items = this._order.map((input) => ({ id: parseInt(input.value, 10), quantity: 1 }));

      if (items.length === 0) {
        return;
      }

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      error.hidden = true;

      try {
        await updateCart('add.js', { items });
      } catch (exception) {
        error.textContent = exception.message;
        error.hidden = false;
        button.disabled = false;
      } finally {
        button.removeAttribute('aria-busy');
      }
    }
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * Product page: 1 / 2 / 3 puzzle bundle picker
   * ---------------------------------------------------------------------------------------------------------------- */

  class QkBundlePicker extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      const signal = this._abortController.signal;

      this._order = [];
      this.addEventListener('click', this._onClick.bind(this), { signal });
      this.addEventListener('change', this._onPickChange.bind(this), { signal });

      if (this.hasAttribute('data-instant')) {
        /* One-tap mode: with 2+ puzzles the regular Add to cart adds the whole set. Listening in the capture phase on
           the document runs before the theme's product form handler, which is then stopped. */
        document.addEventListener('submit', this._onProductSubmit.bind(this), { capture: true, signal });
        document.addEventListener('click', this._onSaveStateClick.bind(this), { capture: true, signal });
        window.addEventListener('pageshow', (event) => event.persisted && sessionStorage.removeItem(RETURN_KEY), { signal });
        this._restoreState();
      }
    }

    get buyButton() {
      return this.closest('.product-info')?.querySelector('.product-info__buy-buttons button[type="submit"]');
    }

    _onProductSubmit(event) {
      const form = event.target;

      if (this.qty === 1 || !form.matches?.('form[action*="/cart/add"]') || !this.closest('.product-info')?.contains(form)) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      this._add();
    }

    /* Links marked data-qk-save-state (bigger bundles block) remember the selection, so "Back to my puzzle selection"
       on the bundles page can bring the shopper back to the same tier, designs and scroll position. */
    _onSaveStateClick(event) {
      if (!event.target.closest('[data-qk-save-state]')) {
        return;
      }

      try {
        sessionStorage.setItem(RETURN_KEY, JSON.stringify({
          path: location.pathname,
          url: location.pathname + location.search,
          qty: this.qty,
          ids: this._order.map((input) => input.value),
          y: Math.round(window.scrollY)
        }));
      } catch (error) {
        // Storage unavailable: the bundles page simply shows no back link
      }
    }

    _restoreState() {
      let state = null;

      try {
        state = JSON.parse(sessionStorage.getItem(RETURN_KEY));
      } catch (error) {
        state = null;
      }

      if (!state || state.path !== location.pathname) {
        return;
      }

      sessionStorage.removeItem(RETURN_KEY);

      if (state.qty > 1) {
        this._order = state.ids.map((id) => this.picks.find((input) => input.value === id)).filter(Boolean);
        this._setQty(state.qty);
      }

      requestAnimationFrame(() => setTimeout(() => window.scrollTo({ top: state.y, behavior: 'instant' }), 150));
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    get qty() {
      return parseInt(this.dataset.qty, 10);
    }

    get picks() {
      return Array.from(this.querySelectorAll('[data-qk-pick]'));
    }

    _onClick(event) {
      const tier = event.target.closest('[data-qk-qty]');

      if (tier && !tier.disabled) {
        this._setQty(parseInt(tier.dataset.qkQty, 10));
      } else if (event.target.closest('[data-qk-choose]')) {
        this._togglePicker(true);
      } else if (event.target.closest('[data-qk-close]')) {
        this._togglePicker(false);
      } else if (event.target.closest('[data-qk-add]')) {
        if (this.hasAttribute('data-instant')) {
          this._togglePicker(false);
        } else {
          this._add();
        }
      }
    }

    _togglePicker(open) {
      const picker = this.querySelector('[data-qk-picker]');
      picker.hidden = !open;

      if (open) {
        picker.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    /* Switch tier; for 2+ preselect the current puzzle and the first suggestions, keeping picks still valid. */
    _setQty(qty) {
      this.dataset.qty = qty;
      this.querySelectorAll('[role="radio"][data-qk-qty]').forEach((tier) => tier.setAttribute('aria-checked', String(tier.dataset.qkQty === String(qty))));
      this.querySelector('[data-qk-panel]').hidden = qty === 1;

      if (qty === 1) {
        this._togglePicker(false);
        this._updateBuyButton();
        return;
      }

      this._order = this._order.slice(0, qty);

      for (const input of this.picks) {
        if (this._order.length >= qty) {
          break;
        }

        if (!this._order.includes(input)) {
          this._order.push(input);
        }
      }

      this.picks.forEach((input) => input.checked = this._order.includes(input));
      this.querySelector('[data-qk-need]').textContent = qty;
      this._render();
    }

    _onPickChange(event) {
      if (event.target.matches('[data-qk-pick]')) {
        this._order = limitPicks(this._order, event.target, this.qty);
        this._render();
      }
    }

    /* Slots show the chosen designs (empty "?" slots for the missing ones) and the button the bundle price. */
    _render() {
      const qty = this.qty;
      const slots = this.querySelector('[data-qk-slots]');

      slots.replaceChildren(...Array.from({ length: qty }, (_, index) => {
        const slot = document.createElement('span');
        const image = this._order[index]?.closest('.qk-upsell__pick').querySelector('img');

        slot.className = `qk-upsell__thumb${image ? '' : ' qk-upsell__thumb--empty'}`;

        if (image) {
          slot.appendChild(image.cloneNode());
        } else {
          slot.textContent = '?';
        }

        return slot;
      }).flatMap((slot, index) => {
        if (index === 0) {
          return [slot];
        }

        const plus = document.createElement('span');
        plus.className = 'qk-upsell__plus';
        plus.setAttribute('aria-hidden', 'true');
        plus.textContent = '+';
        return [plus, slot];
      }));

      const button = this.querySelector('[data-qk-add]');
      const missing = qty - this._order.length;
      const bundleQty = parseInt(this.dataset.bundleQty, 10);
      const discount = parseInt(this.dataset.bundleDiscount, 10);
      const isBundle = qty >= bundleQty;
      const total = sumPrices(this._order) - (isBundle ? discount : 0);
      const freeShipping = total >= parseInt(this.dataset.freeShippingThreshold, 10);
      const shippingText = freeShipping ? 'FREE Shipping' : '+ shipping';

      /* Offer card, as in the cart drawer: "best value" (yellow) for the bundle, plain card with a nudge below it */
      const offer = this.querySelector('[data-qk-offer]');
      offer.classList.toggle('qk-upsell__offer--best', isBundle);
      offer.classList.toggle('qk-upsell__offer--small', !isBundle);
      this.querySelector('[data-qk-choose-label]').textContent = `Choose your ${qty} designs`;
      this.querySelector('[data-qk-best-tag]').hidden = !isBundle;
      this.querySelector('[data-qk-free-tag]').hidden = !freeShipping;

      this.querySelector('[data-qk-title]').textContent = isBundle
        ? `Get ${qty} puzzles for just ${formatMoney(total)}!`
        : `${qty} puzzles for ${formatMoney(total)}`;

      this.querySelector('[data-qk-text]').textContent = isBundle
        ? `Mix & match any ${qty} designs · Save ${formatMoney(discount)} · ${shippingText}`
        : `${shippingText} · Add ${bundleQty - qty} more to save ${formatMoney(discount)}`;

      const nudge = this.querySelector('[data-qk-nudge]');
      nudge.hidden = isBundle || discount <= 0;
      nudge.textContent = `Add ${bundleQty - qty} more & save ${formatMoney(discount)}`;

      if (this.hasAttribute('data-instant')) {
        button.disabled = missing > 0;
        this.querySelector('[data-qk-add-label]').textContent = missing > 0 ? `Choose ${missing} more ${missing === 1 ? 'design' : 'designs'}` : 'Done';
        this._updateBuyButton(missing > 0 ? null : `Add ${qty} puzzles · ${formatMoney(total)}`);
        return;
      }

      button.disabled = missing > 0;
      this.querySelector('[data-qk-add-label]').textContent = missing > 0
        ? `Choose ${missing} more ${missing === 1 ? 'design' : 'designs'}`
        : `Add ${qty} puzzles · ${formatMoney(total)}`;
    }

    /* Instant mode: the regular Add to cart shows the set and its price (label restored for 1 puzzle). */
    _updateBuyButton(label) {
      const button = this.buyButton;

      if (!button) {
        return;
      }

      const textHolder = button.querySelector('div') || button;
      this._buyLabel ??= textHolder.textContent.trim();
      textHolder.textContent = this.qty === 1 ? this._buyLabel : (label || this._buyLabel);
      button.disabled = this.qty > 1 && !label;

      /* The theme's sticky add-to-cart bar submits the same form; give its buttons the same label. */
      document.querySelectorAll('.product-quick-add button[type="submit"]').forEach((stickyButton) => {
        const stickyText = stickyButton.querySelector('div') || stickyButton;
        stickyButton.dataset.qkLabel ??= stickyText.textContent.trim();
        stickyText.textContent = this.qty === 1 ? stickyButton.dataset.qkLabel : (label || stickyButton.dataset.qkLabel);
        stickyButton.disabled = button.disabled;
      });
    }

    async _add() {
      const instant = this.hasAttribute('data-instant');
      const button = instant ? this.buyButton : this.querySelector('[data-qk-add]');
      const error = this.querySelector(instant ? '[data-qk-instant-error]' : '.qk-upsell__error');
      const items = this._order.map((input) => ({ id: parseInt(input.value, 10), quantity: 1 }));

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      error.hidden = true;

      try {
        await updateCart('add.js', { items }, { openDrawer: true });
      } catch (exception) {
        error.textContent = exception.message;
        error.hidden = false;
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * Cart drawer: swap a line for another design
   * ---------------------------------------------------------------------------------------------------------------- */

  class QkLineSwap extends HTMLElement {
    connectedCallback() {
      this._abortController = new AbortController();
      this.querySelector('select')?.addEventListener('change', this._onChange.bind(this), { signal: this._abortController.signal });
    }

    disconnectedCallback() {
      this._abortController.abort();
    }

    async _onChange(event) {
      const select = event.target;
      const newVariantId = select.value;
      const quantity = parseInt(this.dataset.quantity, 10);

      select.disabled = true;
      this.closest('.line-item')?.querySelector('pill-loader')?.setAttribute('aria-busy', 'true');

      try {
        /* If the new design is already in the cart, merge both quantities on that line. */
        const cart = await (await fetch(`${routesRoot()}cart.js`)).json();
        const existingQuantity = cart.items
          .filter((item) => String(item.variant_id) === newVariantId && item.key !== this.dataset.lineKey)
          .reduce((total, item) => total + item.quantity, 0);

        await updateCart('update.js', {
          updates: {
            [this.dataset.lineKey]: 0,
            [newVariantId]: existingQuantity + quantity
          }
        });
      } catch (exception) {
        select.disabled = false;
        select.value = select.querySelector('option[selected]')?.value;
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } finally {
        this.closest('.line-item')?.querySelector('pill-loader')?.removeAttribute('aria-busy');
      }
    }
  }

  if (!window.customElements.get('qk-variation-picker')) {
    window.customElements.define('qk-variation-picker', QkVariationPicker);
  }

  if (!window.customElements.get('qk-cart-upsell')) {
    window.customElements.define('qk-cart-upsell', QkCartUpsell);
  }

  if (!window.customElements.get('qk-bundle-picker')) {
    window.customElements.define('qk-bundle-picker', QkBundlePicker);
  }

  if (!window.customElements.get('qk-line-swap')) {
    window.customElements.define('qk-line-swap', QkLineSwap);
  }
})();
